import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppButton,
  Box,
  ButtonResponsiveWrapper,
  FormProvider,
  Preloader,
  useResponsive,
} from '@common-fe/common-fe';

import { ModalWrapper } from '@/components/wrappers';
import { useSnackbar } from '@/modules/core/hooks';

import { useCreateProviderQuery } from '../useProviders.query';

import AddProviderForm from './AddProviderForm';
import useAddProviderForm, { AppProviderField, AppProviderForm } from './useAddProviderForm';

interface Props {
  onSetVisible: (value: boolean) => void;
}

const AddProviderModal: React.FC<Props> = ({ onSetVisible }) => {
  const { t } = useTranslation();
  const { save, isLoading } = useCreateProviderQuery();
  const { methods } = useAddProviderForm();
  const { handleAddPermanentSnackbar } = useSnackbar();

  const handleClose = useCallback(() => {
    onSetVisible(false);
  }, [onSetVisible]);

  const handleSuccess = useCallback(
    (providerName?: string) => {
      if (providerName) {
        handleAddPermanentSnackbar({
          text: `${providerName} ${t('added')}.`,
          closeIcon: true,
        });
      }
      handleClose();
    },
    [t, handleAddPermanentSnackbar, handleClose]
  );

  const handleSubmit = useCallback(
    async (formValues: AppProviderForm) => {
      const createdProvider = await save({ name: formValues[AppProviderField.provideName] });
      handleSuccess(createdProvider?.facility_name);
    },
    [save, handleSuccess]
  );

  const { isMobile } = useResponsive();
  return (
    <ModalWrapper
      testId="add-provider"
      visible
      onSetVisible={onSetVisible}
      title={t('Add new Provider/Merchant')}
      titleBoxStyle={{ paddingBottom: 0 }}
    >
      <Box margin={{ top: 'spacing24' }} background="module" pad="spacing24" round="moduleRound">
        <FormProvider {...methods}>
          <AddProviderForm />
        </FormProvider>
      </Box>

      <ButtonResponsiveWrapper margin={{ top: 'spacing24' }} justify="end">
        <Box width={{ min: '150px' }}>
          <AppButton testId="add-provider-cancel" width="100%" buttonType="secondary" onClick={handleClose}>
            {t('Cancel')}
          </AppButton>
        </Box>
        <Box width={{ min: '150px' }} onClick={methods.handleSubmit(handleSubmit)}>
          <AppButton testId="add-provider-save" width="100%" disabled={isLoading}>
            {isLoading ? <Preloader color="white" /> : t('Save')}
          </AppButton>
        </Box>
      </ButtonResponsiveWrapper>
    </ModalWrapper>
  );
};

export default AddProviderModal;
